import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Money } from "../_shared";

export default function StepPricing({ q, setQ, qid, save }) {
  const upd = (k, v) => setQ((p) => ({ ...p, pricing: { ...p.pricing, [k]: Number(v) || 0 } }));
  const [view, setView] = useState("client");
  const nav = useNavigate();
  const finalize = async () => {
    setQ((p) => ({ ...p, status: "finalized" }));
    await save(false);
    if (qid) nav(`/quotations/${qid}/preview`);
  };
  const totalCost = (q.pricing.supplier_cost||0) + (q.pricing.flight_cost||0) + (q.pricing.hotel_cost||0) + (q.pricing.transfer_cost||0) + (q.pricing.sightseeing_cost||0) + (q.pricing.other_cost||0);
  const margin = q.pricing.grand_total - totalCost;
  const marginPct = totalCost > 0 ? ((margin / q.pricing.grand_total) * 100) : 0;
  return (
    <>
      <div className="mb-5 flex items-center gap-2 border border-border rounded p-1 w-fit bg-slate-50">
        <button data-testid="price-view-client" onClick={()=>setView("client")} className={`text-xs uppercase tracking-widest px-4 py-1.5 rounded transition-colors ${view==="client" ? "bg-white text-[#16A34A] shadow-sm" : "text-slate-500"}`}>Client Sheet</button>
        <button data-testid="price-view-cost" onClick={()=>setView("cost")} className={`text-xs uppercase tracking-widest px-4 py-1.5 rounded transition-colors ${view==="cost" ? "bg-white text-[#16A34A] shadow-sm" : "text-slate-500"}`}>Internal Cost Sheet</button>
      </div>

      {view === "client" ? (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Currency</label>
              <select data-testid="price-currency" value={q.pricing.currency} onChange={(e)=>setQ((p)=>({...p, pricing:{...p.pricing, currency: e.target.value}}))} className="w-full border border-border rounded px-3 py-2.5 text-sm bg-white">
                {["INR","USD","EUR","GBP","AED","SGD","AUD"].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <Money label="Package Cost" value={q.pricing.package_cost} onChange={(v)=>upd("package_cost", v)} testid="price-pkg" currency={q.pricing.currency} />
            <Money label="Per Person Cost" value={q.pricing.per_person_cost} onChange={(v)=>upd("per_person_cost", v)} testid="price-pp" currency={q.pricing.currency} />
            <Money label="Taxes" value={q.pricing.taxes} onChange={(v)=>upd("taxes", v)} testid="price-tax" currency={q.pricing.currency} />
            <Money label="TCS" value={q.pricing.tcs} onChange={(v)=>upd("tcs", v)} testid="price-tcs" currency={q.pricing.currency} />
            <Money label="Visa Charges" value={q.pricing.visa_charges} onChange={(v)=>upd("visa_charges", v)} testid="price-visa" currency={q.pricing.currency} />
            <Money label="Markup" value={q.pricing.markup} onChange={(v)=>upd("markup", v)} testid="price-markup" currency={q.pricing.currency} />
            <Money label="Service Charges" value={q.pricing.service_charges} onChange={(v)=>upd("service_charges", v)} testid="price-service" currency={q.pricing.currency} />
            <Money label="Discount" value={q.pricing.discount} onChange={(v)=>upd("discount", v)} testid="price-discount" currency={q.pricing.currency} />
          </div>
          <div className="mt-6 bg-[#16A34A] text-white rounded p-5 flex items-center justify-between">
            <div>
              <div className="overline text-white/70 text-[10px]">Grand Total (Client)</div>
              <div data-testid="price-grand-total" className="font-editorial text-4xl mt-1">{q.pricing.currency} {q.pricing.grand_total.toLocaleString()}</div>
            </div>
            <button data-testid="finalize-btn" onClick={finalize} className="bg-white text-[#16A34A] px-5 py-2.5 rounded font-semibold text-sm active:scale-95 transition-transform">
              Finalize &amp; Preview
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800 mb-5">
            <b>Internal only.</b> These fields are never shown in the client PDF or public share link. Use to track margin per quote.
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Money label="Flight Cost" value={q.pricing.flight_cost} onChange={(v)=>upd("flight_cost", v)} testid="cost-flight" currency={q.pricing.currency} />
            <Money label="Hotel Cost" value={q.pricing.hotel_cost} onChange={(v)=>upd("hotel_cost", v)} testid="cost-hotel" currency={q.pricing.currency} />
            <Money label="Transfer Cost" value={q.pricing.transfer_cost} onChange={(v)=>upd("transfer_cost", v)} testid="cost-transfer" currency={q.pricing.currency} />
            <Money label="Sightseeing Cost" value={q.pricing.sightseeing_cost} onChange={(v)=>upd("sightseeing_cost", v)} testid="cost-sight" currency={q.pricing.currency} />
            <Money label="Supplier Cost" value={q.pricing.supplier_cost} onChange={(v)=>upd("supplier_cost", v)} testid="cost-supplier" currency={q.pricing.currency} />
            <Money label="Other Cost" value={q.pricing.other_cost} onChange={(v)=>upd("other_cost", v)} testid="cost-other" currency={q.pricing.currency} />
          </div>
          <div className="mt-4">
            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Internal Notes</label>
            <textarea data-testid="cost-notes" rows={3} value={q.pricing.internal_notes || ""} onChange={(e)=>setQ((p)=>({...p, pricing:{...p.pricing, internal_notes: e.target.value}}))} className="w-full border border-border rounded px-3 py-2 text-sm" />
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3">
            <div className="border border-border rounded p-4 bg-white">
              <div className="overline text-[9px]">Total Cost</div>
              <div data-testid="cost-total" className="font-editorial text-2xl mt-1">{q.pricing.currency} {totalCost.toLocaleString()}</div>
            </div>
            <div className="border border-border rounded p-4 bg-white">
              <div className="overline text-[9px]">Grand Total (Client)</div>
              <div className="font-editorial text-2xl mt-1">{q.pricing.currency} {q.pricing.grand_total.toLocaleString()}</div>
            </div>
            <div className={`rounded p-4 ${margin >= 0 ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}>
              <div className={`overline text-[9px] ${margin >= 0 ? "!text-emerald-700" : "!text-red-700"}`}>Margin</div>
              <div data-testid="cost-margin" className="font-editorial text-2xl mt-1">{q.pricing.currency} {margin.toLocaleString()}</div>
              <div className="text-xs mt-0.5 text-slate-500">{marginPct.toFixed(1)}%</div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
